import {
  FunctionComponent,
  useEffect,
  useState,
  useCallback,
  FormEvent,
  useRef,
  Fragment,
  CSSProperties,
} from 'react';
import {
  getCategories,
  getOrder,
  getQuestions,
  getRoomInfo,
  updateOrder,
} from '../api';
import {
  QuestionType,
  CategoryType,
  RoomType,
  DefaultValue,
  OrderType,
  CategoryEnum,
  OrderError,
} from './../types';
import 'noty/lib/noty.css';
import Category from './Category';
import { Dictionary } from 'lodash';
import groupBy from 'lodash.groupby';
import { useCategoriesOfferContext } from '../context/CategoriesOfferContext';
import Summary from './Summary';
import { scrollToElement } from '../scroll';
import CategoryControls from './CategoryControls';
import CategoryInfo from './CategoryInfo';

const Calculator: FunctionComponent<{
  customer: string;
  isModal?: boolean;
  title?: string;
  subtitle?: string;
}> = ({ customer, isModal, title, subtitle }) => {
  const [questions, setQuestions] = useState<Dictionary<QuestionType[]>>();
  const [categories, setCategories] = useState<CategoryType[]>([]);
  const [width, setWidth] = useState<number | string>('');
  const [height, setHeight] = useState<number | string>('');
  const [depth, setDepth] = useState<number | string>('');
  const [year, setYear] = useState<string>();
  const [room, setRoom] = useState<RoomType>();
  const [order, setOrder] = useState<OrderType>();
  const [disabled, setDisabled] = useState<boolean>(true);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [errors, setErrors] = useState({
    width: false,
    height: false,
    depth: false,
    selectedCategory: false,
    year: false,
  });
  const isValidationTriggered = useRef<boolean>(false);
  const {
    removeData,
    setOrderId,
    setEditMode,
    editMode,
    setRotAvdrag,
    setRoomInfo,
    setWpId,
    orderId,
    setQuestionErrors,
    loading,
  } = useCategoriesOfferContext();
  const calcRef = useRef<HTMLDivElement>(null);

  const handleErrors = useCallback(() => {
    if (errors.width || errors.height || errors.depth)
      scrollToElement(calcRef?.current, '.calculator_card-dimensions');
    else if (errors.year)
      scrollToElement(calcRef?.current, '.calculator_question-years', 'center');
    else if (errors.selectedCategory)
      scrollToElement(calcRef?.current, '.calculator_card-category');
  }, [errors]);

  const clearError = useCallback(
    (errorName: string) => {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [errorName]: false,
      }));
    },
    [setErrors]
  );

  const handleInputChange = useCallback(
    (e: FormEvent<HTMLInputElement>) => {
      const inputName = e.currentTarget.name;
      let inputValue = e.currentTarget.value;

      clearError(inputName);

      switch (inputName) {
        case 'width':
          setWidth(inputValue !== '' ? parseFloat(inputValue) : '');
          break;
        case 'height':
          setHeight(inputValue !== '' ? parseFloat(inputValue) : '');
          break;
        case 'depth':
          setDepth(inputValue !== '' ? parseFloat(inputValue) : '');
          break;
        default:
          break;
      }
    },
    [clearError]
  );

  const validateQuestions = () => {
    const root = calcRef?.current || document;

    let errors: number[] = [];
    Object.entries(questions || {})
      .filter((q) => selectedCategories.includes(q[0]))
      .forEach((item) => {
        item[1].forEach((q) => {
          if (!q.key.startsWith('count_')) {
            const value = (
              root.querySelector(
                `input[name="${q.id + q.key}"]:checked`
              ) as HTMLInputElement
            )?.value;
            if (!value) {
              errors.push(q.id);
            }
          }
        });
      });

    if (errors.length) {
      scrollToElement(calcRef?.current, `.question-${errors[0]}`, 'center');
      setQuestionErrors(errors);
      return false;
    } else {
      return true;
    }
  };

  const validate = () => {
    const errors = {
      width: typeof width !== 'number' || width < 150 || width > 15000,
      height: typeof height !== 'number' || height < 150 || height > 5000,
      depth: typeof depth !== 'number' || depth < 150 || depth > 15000,
      selectedCategory: !selectedCategories || selectedCategories.length === 0,
      year: !year || year.length === 0,
    };

    setErrors(errors);

    isValidationTriggered.current = true;
    if (Object.values(errors).some((e) => e)) {
      return false;
    } else {
      return validateQuestions();
    }
  };

  useEffect(() => {
    if (isValidationTriggered.current) {
      handleErrors();
      isValidationTriggered.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const isOrder = (o: OrderType | OrderError): o is OrderType => {
    return (o as OrderType).id !== undefined;
  };

  const loadOrder = useCallback(
    async (hash: string) => {
      const o = await getOrder(hash);
      if (isOrder(o)) {
        setEditMode(true);
        setOrder(o);
        setOrderId(o.id);
        setWpId(o.wp_id);
        const {
          width,
          height,
          depth,
          year,
          floorArea,
          wallArea,
          runningMeter,
          customer,
          id,
        } = o;
        setRoom({
          width,
          height,
          depth,
          year,
          preliminary: undefined,
          floorArea,
          wallArea,
          runningMeter,
          baseOffert: undefined,
          customer,
          id,
        });
        setDisabled(false);
      } else {
        console.warn(o.message);
      }
    },
    [setEditMode, setOrderId, setWpId]
  );

  useEffect(() => {
    if (
      typeof width === 'number' &&
      typeof height === 'number' &&
      typeof depth === 'number' &&
      year
    ) {
      setRoomInfo({ width, height, depth, year });
    }
  }, [depth, height, setRoomInfo, width, year]);

  useEffect(() => {
    if (order) {
      setWidth(order.width);
      setHeight(order.height);
      setDepth(order.depth);
      setYear(order.year);
      setRoom(order);
      setRotAvdrag(order.rot);
      const activeCategories = categories
        .filter((c) => order[c.key])
        .map((c) => c.key.toString());
      setSelectedCategories(activeCategories);
    }
  }, [categories, order, questions, setRotAvdrag]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const orderHash = urlParams.get('order');
    if (orderHash && questions) {
      loadOrder(orderHash);
    }
  }, [loadOrder, questions, setEditMode]);

  const fetchQuestions = async () => {
    const q = await getQuestions();
    setQuestions(groupBy(q, (e) => e.category));
  };

  const fetchCategories = async () => {
    const c = await getCategories();
    setCategories(c);
  };

  const handleCategoryClick = (categoryKey: string, categoryLabel: string) => {
    if (selectedCategories.includes(categoryKey)) {
      setSelectedCategories(
        selectedCategories.filter((id) => id !== categoryKey)
      );
      removeData(categoryLabel);
    } else {
      clearError('selectedCategory');
      setSelectedCategories([...selectedCategories, categoryKey]);
    }
  };

  const base = useCallback(async () => {
    if (
      typeof width !== 'number' ||
      typeof height !== 'number' ||
      typeof depth !== 'number' ||
      width === 0 ||
      height === 0 ||
      depth === 0
    )
      return;
    if (orderId && year) {
      try {
        await updateOrder({
          orderId,
          roomInfo: {
            width,
            height,
            depth,
            year,
          },
        });
        setRoom({
          ...room,
          width,
          height,
          depth,
          year,
        } as RoomType);
      } catch (e) {
        console.warn(e);
      }
    } else if (height && width && depth && year) {
      try {
        const {
          preliminary,
          floorArea,
          wallArea,
          runningMeter,
          baseOffert,
          id,
        } = await getRoomInfo({
          width,
          depth,
          height,
          year,
          customer,
        });
        setRoom({
          width,
          height,
          depth,
          year,
          preliminary,
          floorArea,
          wallArea,
          runningMeter,
          baseOffert,
          customer,
          id,
        });
        if (id) setOrderId(id);
        setDisabled(false);
      } catch (e) {
        console.warn(e);
      }
    } else {
      setRoom(undefined);
      setDisabled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, depth, height, width, year]);

  const setAnswer = (q: QuestionType, answer?: string) => {
    if (q.category.toString() === 'base' && q.key === 'year') {
      clearError('year');
      setYear(answer);
    }
  };

  const getDefaultValues = useCallback(
    (key: string): Array<DefaultValue> => {
      const categoryKey = key as unknown as CategoryEnum;
      if (order && questions && key === 'base') {
        return questions.base.map((question) => ({
          question,
          answer: order.year, // TODO fix if more than one "base" question in the future
        }));
      } else if (order && questions && order[categoryKey] !== null) {
        return questions[categoryKey].map((question) => ({
          question,
          answer: order[categoryKey]![
            question.key as unknown as CategoryEnum
          ] as unknown as string,
        }));
      }
      return [];
    },
    [order, questions]
  );

  useEffect(() => {
    fetchCategories();
    fetchQuestions();
  }, []);

  useEffect(() => {
    if (!editMode) {
      base();
    }
  }, [year, width, depth, height, base, editMode]);

  // use effect event key down handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        document?.activeElement instanceof HTMLInputElement &&
        document.activeElement.name === 'height' &&
        e.key === 'Enter'
      ) {
        e.preventDefault();
        // close keyboard on mobile
        (document?.activeElement as HTMLInputElement).blur();
        setTimeout(() => {
          scrollToElement(
            calcRef?.current,
            '.calculator_question-years',
            'center'
          );
        }, 200);
      }
    };

    document?.addEventListener('keydown', handleKeyDown);

    return () => {
      document?.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const modalStyling = {
    overflow: 'auto',
    zIndex: 1,
    padding: '0 10px',
    inset: 0,
    height: '76dvh',
  };

  return (
    <div style={isModal ? (modalStyling as CSSProperties) : {}}>
      <div
        ref={calcRef}
        className={`calculator ${loading ? 'calculator_disabled' : ''}`}
      >
        {title && subtitle ? (
          <div className="calculator_titles">
            <h1 className="calculator_title">{title}</h1>
            <p className="calculator_subtitle">{subtitle}</p>
          </div>
        ) : null}
        <div className="calculator_card calculator_card-dimensions">
          <div className="calculator_wrapper">
            <h2 className="calculator_category_title text-center">
              Ange rummets mått i centimeter
            </h2>
            <CategoryInfo info="Minsta bredd: 150cm, längd: 150cm, höjd: 150cm. Högsta bredd: 15000cm, längd: 15000cm, höjd: 5000cm." />
            <div className="calculator_question_wrapper">
              <div className="calculator_dimensions_buttonGroup">
                <label className="calculator_dimensions_label">Bredd</label>
                <div>
                  <input
                    onChange={handleInputChange}
                    name="width"
                    type="tel"
                    value={width}
                    className="calculator_dimensions_button_input"
                  ></input>
                </div>
              </div>
              <div className="calculator_dimensions_buttonGroup">
                <label className="calculator_dimensions_label">Djup</label>
                <div>
                  <input
                    onChange={handleInputChange}
                    name="depth"
                    type="tel"
                    value={depth}
                    className="calculator_dimensions_button_input"
                  ></input>
                </div>
              </div>
              <div className="calculator_dimensions_buttonGroup">
                <label className="calculator_dimensions_label">Höjd</label>
                <div>
                  <input
                    onChange={handleInputChange}
                    name="height"
                    type="tel"
                    value={height}
                    className="calculator_dimensions_button_input"
                  ></input>
                </div>
              </div>
            </div>
          </div>
          {errors.width || errors.height || errors.depth ? (
            <span className="calculator_error">
              Ange giltiga mått för rummet
            </span>
          ) : null}
          <CategoryControls customer={customer} />
        </div>
        {questions &&
          Object.entries(questions)
            .filter((q) => selectedCategories.includes(q[0]) || q[0] === 'base')
            .map((item, i, arr) =>
              item[0] === 'base' ? (
                <Fragment key={i}>
                  <Category
                    defaultValue={getDefaultValues(item[0])}
                    setAnswer={setAnswer}
                    questions={item[1]}
                    label="Årtal fastighet"
                    className="calculator_question-years"
                    key={item[0]}
                    disabled={disabled}
                    room={room}
                    category={categories?.find(
                      (c) => c.key.toString() === item[0]
                    )}
                    customer={customer}
                    yearError={errors.year}
                  />
                  <div className="calculator_card calculator_card-category">
                    <div className="calculator_wrapper">
                      <h2 className="text-center calculator_category_title">
                        Välj vad du behöver måla
                      </h2>
                      <div className="calculator_paint_wrapper mw-t">
                        {categories
                          .filter(
                            (category) => category.label.toUpperCase() !== 'BAS'
                          )
                          .map((category) => (
                            <div
                              key={category.key}
                              className="calculator_paint_radio_wrapper"
                            >
                              <button
                                className={`calculator_paint_radio ${
                                  selectedCategories.includes(
                                    category.key.toString()
                                  )
                                    ? 'calculator_paint_radio_active'
                                    : ''
                                }`}
                                onClick={() =>
                                  handleCategoryClick(
                                    category.key.toString(),
                                    category.label
                                  )
                                }
                                value={category.key}
                              >
                                <div className="calculator_paint_radio_check">
                                  {category.label}
                                  {selectedCategories.includes(
                                    category.key.toString()
                                  ) ? (
                                    <div className="calculator_paint_radio_circle">
                                      <img
                                        src="https://painting-frontend.vercel.app/check.svg"
                                        alt="plus"
                                        className="calculator_paint_radio_svg_active"
                                      />
                                    </div>
                                  ) : (
                                    <img
                                      src="https://painting-frontend.vercel.app/plus.svg"
                                      alt="plus"
                                      className="calculator_paint_radio_svg"
                                    />
                                  )}
                                </div>
                              </button>
                            </div>
                          ))}
                      </div>
                      {errors.selectedCategory && (
                        <span className="calculator_qerror">
                          Välj vad du behöver måla
                        </span>
                      )}
                    </div>
                    <CategoryControls customer={customer} />
                  </div>
                </Fragment>
              ) : (
                <Category
                  setAnswer={setAnswer}
                  questions={item[1]}
                  defaultValue={getDefaultValues(item[0])}
                  key={item[0]}
                  disabled={disabled}
                  room={room}
                  customer={customer}
                  category={categories?.find(
                    (c) => c.key.toString() === item[0]
                  )}
                />
              )
            )}
        <Summary validate={validate} customer={customer} />
      </div>
    </div>
  );
};

export default Calculator;
